import React, { useRef, useState, useEffect, useMemo } from "react";
import Moveable from "react-moveable";
import store from "@/stores/clip/player";
import trackStore from "@/stores/clip/trackState";
import { defaultMoveOptions } from "@/stores/clip/data/constant";
import { observer } from "mobx-react";
import { toJS } from "mobx";

interface PlayerMoveableProps {
  style?: React.CSSProperties;
  canvasSize?: {
    width: number;
    height: number;
  };
}

interface TargetItem {
  id: string;
  lineIndex: number;
  itemIndex: number;
  y: number;
  x: number;
  w: number;
  h: number;
  scale: number;
  left: number;
  top: number;
}

const PlayerMoveable: React.FC<PlayerMoveableProps> = ({
  style = {},
  canvasSize,
}) => {
  const moveContainer = useRef<HTMLDivElement>(null);
  const moveable = useRef<Moveable>(null);
  const [moveTarget, setMoveTarget] = useState<HTMLElement | null>(null);

  // 计算目标列表
  const targetList = useMemo(() => {
    if (
      store.playerConfig.playerHeight === 0 &&
      store.playerConfig.playerWidth === 0
    ) {
      return [];
    }

    const layerArr: TargetItem[] = [];

    trackStore.trackList.forEach(({ groups }, lineIndex) => {
      const index = groups.findIndex((item: Record<string, any>, itemIndex) => {
        if (
          store.playStartFrame >= item.start &&
          store.playStartFrame <= item.end &&
          item.draw
        ) {
          return true;
        }
        return false;
      });
      const trackItem = groups[index] as any;

      if (trackItem) {
        layerArr.unshift({
          lineIndex,
          itemIndex: index,
          id: trackItem.id,
          scale: trackItem.scale / 100,
          x: trackItem.centerX,
          y: trackItem.centerY,
          w: trackItem.width,
          h: trackItem.height,
          left: store.playerConfig.playerWidth / 2 - trackItem.width / 2,
          top: store.playerConfig.playerHeight / 2 - trackItem.height / 2,
        });
      }
    });

    if (moveable.current) {
      moveable.current.updateRect();
    }

    return layerArr;
  }, [
    store.playerConfig.playerHeight,
    store.playerConfig.playerWidth,
    store.playStartFrame,
    toJS(trackStore.trackList),
  ]);

  // 拖拽选项
  const draggableOptions = useMemo(() => {
    console.log(
      "🚀 ~ draggableOptions ~ moveTarget:",
      moveTarget,
      moveContainer.current
    );

    return {
      target: moveTarget,
      className: "cc-move",
      container: moveContainer.current,
      ...defaultMoveOptions,
    };
  }, [moveTarget, moveContainer.current]);

  // 选择元素
  const selectItem = (eleid: string) => {
    console.log("🚀 ~ selectItem ~ eleid:", eleid);
    store.isPause = true;
    trackStore.selectGroupById(eleid);
  };

  // 拖拽事件
  const onDrag = (params: any) => {
    console.log("🚀 ~ onDrag ~ params:", params);

    const { target, transform, translate } = params;
    const { lineindex, itemindex } = target.dataset;
    const [x, y] = translate;

    // (trackStore.trackList[lineindex].groups[itemindex] as any).centerX = x;
    // (trackStore.trackList[lineindex].groups[itemindex] as any).centerY = y;
    trackStore.updateGroup(lineindex, itemindex, {
      centerX: x,
      centerY: y,
    });
    target.style.transform = transform;
  };

  // 缩放事件
  const onScale = (params: any) => {
    const { target, scale, transform } = params;
    const { lineindex, itemindex } = target.dataset;
    const newScale = Math.max(Math.round(scale[0] * 100), 1);
    (trackStore.trackList[lineindex].groups[itemindex] as any).scale = newScale;
    target.style.transform = transform;
  };

  // 鼠标按下事件
  const mousedown = (event: React.MouseEvent, eleid: string) => {
    event.stopPropagation();
    store.isPause = true;
    trackStore.selectGroupById(eleid);
    setMoveTarget(event.currentTarget as HTMLElement);
    if (moveable.current) {
      moveable.current.dragStart(event as unknown as MouseEvent);
    }
  };

  // 监听选中项和目标列表的变化
  useEffect(() => {
    if (
      moveContainer.current &&
      trackStore.selectGroupItem.line !== -1 &&
      trackStore.selectGroupItem.index !== -1
    ) {
      const targetTrack =
        trackStore.trackList[trackStore.selectGroupItem.line].groups[
          trackStore.selectGroupItem.index
        ];
      if (
        targetTrack &&
        targetList.find((item) => item.id === targetTrack.id)
      ) {
        setMoveTarget(
          moveContainer.current.querySelector(
            `.move-target[data-eleid='${targetTrack.id}']`
          ) as HTMLElement
        );
      } else {
        setMoveTarget(null);
      }
    } else {
      setMoveTarget(null);
    }
  }, [toJS(trackStore.selectGroupItem), targetList]);

  return (
    <div
      ref={moveContainer}
      className="moveContainer absolute left-0 right-0 top-0 bottom-0 m-auto"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        margin: "auto",
        ...style,
      }}
    >
      {/* {JSON.stringify(draggableOptions)} */}
      {targetList.map((item, index) => (
        <div
          key={item.id}
          data-eleid={item.id}
          data-lineindex={item.lineIndex}
          data-itemindex={item.itemIndex}
          style={{
            position: "absolute",
            zIndex: index,
            top: `${item.top}px`,
            left: `${item.left}px`,
            width: `${item.w}px`,
            height: `${item.h}px`,
            transform: `translate(${item.x}px, ${item.y}px) scale(${item.scale})`,
          }}
          className="move-target absolute"
          onClick={(e) => {
            e.stopPropagation();
            selectItem(item.id);
          }}
          onMouseDown={(e) => mousedown(e, item.id)}
          onMouseUp={() => {
            console.log("up");
          }}
        />
      ))}
      {/* <Moveable
        ref={moveable}
        {...draggableOptions}
        onDrag={onDrag}
        onScale={onScale}
      /> */}
    </div>
  );
};

export default observer(PlayerMoveable);
